import {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import {Transition} from "@headlessui/react";
import {FaEye, FaEyeSlash} from "react-icons/fa";
import {TbTableOptions} from "react-icons/tb";
import Button from "../../button/button.jsx";

const TableColumnVisibility = ({columns, setColumns, onOpenChange}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [truncatedColumns, setTruncatedColumns] = useState({});
    const labelRefs = useRef({});
    const menuRef = useRef(null);

    const updateOpen = (nextOpen) => {
        setIsOpen(nextOpen);
        onOpenChange?.(nextOpen);
    };

    useEffect(() => {
        const closeOtherDropdown = (event) => {
            if (event.detail === 'columns') {
                return;
            }

            setIsOpen(false);
        };

        document.addEventListener('report-dropdown-open', closeOtherDropdown);

        return () => {
            document.removeEventListener('report-dropdown-open', closeOtherDropdown);
        };
    }, []);
    const containerRef = useRef(null);

    const measureLabelOverflow = useCallback(() => {
        const nextTruncatedColumns = {};

        Object.entries(columns ?? {})
            .filter(([, column]) => column.exportOnly !== true)
            .forEach(([columnId]) => {
                const labelElement = labelRefs.current[columnId];
                nextTruncatedColumns[columnId] = Boolean(labelElement && labelElement.scrollWidth > labelElement.clientWidth);
            });

        setTruncatedColumns(previousColumns => {
            const previousIds = Object.keys(previousColumns);
            const nextIds = Object.keys(nextTruncatedColumns);
            const hasChanged = previousIds.length !== nextIds.length || nextIds.some(columnId => previousColumns[columnId] !== nextTruncatedColumns[columnId]);

            return hasChanged ? nextTruncatedColumns : previousColumns;
        });
    }, [columns]);

    useLayoutEffect(() => {
        if (!isOpen) return;

        let firstFrame;
        let secondFrame;

        const measureAfterLayout = () => {
            measureLabelOverflow();
            secondFrame = requestAnimationFrame(measureLabelOverflow);
        };

        firstFrame = requestAnimationFrame(measureAfterLayout);

        const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measureLabelOverflow);
        if (resizeObserver) {
            Object.values(labelRefs.current).forEach(labelElement => resizeObserver.observe(labelElement));
        }

        window.addEventListener("resize", measureLabelOverflow);

        return () => {
            cancelAnimationFrame(firstFrame);
            cancelAnimationFrame(secondFrame);
            resizeObserver?.disconnect();
            window.removeEventListener("resize", measureLabelOverflow);
        };
    }, [isOpen, measureLabelOverflow]);

    const toggleColumn = (columnId) => {
        setColumns(previousColumns => ({
            ...previousColumns,
            [columnId]: {
                ...previousColumns[columnId],
                visible: !previousColumns[columnId].visible
            }
        }));
    };

    useEffect(() => {
        if (!isOpen) return;

        const closeMenu = (event) => {
            if (!containerRef.current?.contains(event.target)) setIsOpen(false);
        };

        const closeOnEscape = (event) => {
            if (event.key === "Escape") setIsOpen(false);
        };

        document.addEventListener("mousedown", closeMenu);
        document.addEventListener("keydown", closeOnEscape);

        return () => {
            document.removeEventListener("mousedown", closeMenu);
            document.removeEventListener("keydown", closeOnEscape);
        };
    }, [isOpen]);

    return (
        <div ref={containerRef} className="relative print:hidden">
            <Button type="button" target="primary-outline" title="Configurar colunas" className="w-[32px] p-0!" onClick={() => updateOpen(!isOpen)}>
                <TbTableOptions size={18}/>
            </Button>

            <Transition show={isOpen} enter="transition ease-out duration-75" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="transition ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <div ref={menuRef} role="menu" className="absolute right-0 top-full z-50 mt-1 w-64 max-h-[calc(100vh-5rem)] origin-top-right overflow-y-auto overscroll-contain rounded-md border border-gray-300 bg-white shadow">
                    <div className="space-y-0.5 p-1.5">
                        {Object.entries(columns ?? {})
                            .filter(([, column]) => column.exportOnly !== true)
                            .map(([columnId, column]) => (
                            <button
                                key={columnId}
                                type="button"
                                role="menuitemcheckbox"
                                aria-checked={column.visible}
                                title={truncatedColumns[columnId] ? column.label : undefined}
                                className={`flex min-w-0 w-full cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-[14px] transition-colors hover:bg-gray-100 ${column.visible ? "font-medium text-gray-700" : "text-gray-400"}`}
                                onClick={() => toggleColumn(columnId)}
                            >
                                <span
                                    ref={labelElement => {
                                        if (labelElement) {
                                            labelRefs.current[columnId] = labelElement;
                                        } else {
                                            delete labelRefs.current[columnId];
                                        }
                                    }}
                                    className="min-w-0 flex-1 truncate"
                                >
                                    {column.label}
                                </span>
                                {column.visible ? <FaEye className="shrink-0 text-(--blue-dark)"/> : <FaEyeSlash className="shrink-0 text-gray-400"/>}
                            </button>
                        ))}
                    </div>
                </div>
            </Transition>
        </div>
    );
};

export default TableColumnVisibility;